/**
 * LocationsService - Handles Shopify Storefront API communication and data management
 *
 * Responsibilities:
 * - Fetch locations from Shopify Storefront API
 * - Cache location data in sessionStorage (5-minute TTL)
 * - Transform GraphQL responses to standardized format
 * - Calculate store open/closed status
 * - Provide utility functions for formatting
 */

import { createTranslator } from './translator.js';

/**
 * Utility: Escape HTML to prevent XSS
 */
export function escapeHtml(text) {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Utility: Format time from 24h to 12h format
 */
export function formatTime(time) {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'pm' : 'am';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
}

/**
 * Utility: Format phone number based on country code
 */
export function formatPhoneNumber(phone, countryCode) {
  if (!phone) return '';

  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');

  // Format based on country
  switch (countryCode) {
    case 'us':
    case 'ca':
      // Format as (XXX) XXX-XXXX
      if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
      } else if (digits.length === 11 && digits[0] === '1') {
        return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
      }
      break;
    case 'au':
      // Format as +61 X XXXX XXXX
      if (digits.length === 10 && digits.startsWith('0')) {
        return `+61 ${digits.slice(1, 2)} ${digits.slice(2, 6)} ${digits.slice(6)}`;
      } else if (digits.length === 11 && digits.startsWith('61')) {
        return `+${digits.slice(0, 2)} ${digits.slice(2, 3)} ${digits.slice(3, 7)} ${digits.slice(7)}`;
      }
      break;
  }

  return phone; // Return original if no formatting rules match
}

/**
 * Utility: Slugify a string for URL-safe format
 * @param {string} text - Text to slugify
 * @returns {string} Slugified text
 */
export function slugify(text) {
  if (!text) return '';

  return text
    .toString()
    .toLowerCase()
    .trim()
    // Remove apostrophes
    .replace(/[']/g, '')
    // Replace spaces and underscores with hyphens
    .replace(/[\s_]+/g, '-')
    // Remove all non-word chars except hyphens
    .replace(/[^\w\-]+/g, '')
    // Replace multiple hyphens with single hyphen
    .replace(/\-\-+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-+/, '')
    .replace(/-+$/, '');
}

/**
 * Utility: Convert Shopify Rich Text metafield schema to HTML
 * @param {Object|string} schema - The schema object or JSON string
 * @param {Object} options - Conversion options
 * @param {string|boolean} options.scoped - Scoped class name or boolean for default 'rte' class
 * @param {Object} options.classes - Tag-specific class names
 * @param {boolean} options.newLineToBreak - Convert \n to <br> tags
 * @returns {string} Converted HTML string
 */
export function convertRichTextToHtml(schema, options = {}) {
  if (!schema) return '';

  let { scoped, classes, newLineToBreak } = options;

  // Parse JSON string if needed
  if (typeof schema === 'string') {
    try {
      schema = JSON.parse(schema);
    } catch (e) {
      console.warn('Failed to parse rich text schema:', e);
      return '';
    }
  }

  // Handle root element
  if (schema.type === 'root' && schema.children) {
    const content = convertChildren(schema.children, options);
    if (scoped) {
      const className = scoped === true ? 'rte' : scoped;
      return `<div class="${escapeHtml(className)}">${content}</div>`;
    }
    return content;
  }

  // Handle array of elements
  if (Array.isArray(schema)) {
    return convertChildren(schema, options);
  }

  return '';
}

/**
 * Convert array of rich text elements to HTML
 * @private
 */
function convertChildren(children, options) {
  if (!children || !Array.isArray(children)) return '';

  return children.map(el => convertElement(el, options)).join('');
}

/**
 * Convert single rich text element to HTML
 * @private
 */
function convertElement(el, options) {
  if (!el || !el.type) return '';

  switch (el.type) {
    case 'paragraph':
      return buildParagraph(el, options);
    case 'heading':
      return buildHeading(el, options);
    case 'list':
      return buildList(el, options);
    case 'list-item':
      return buildListItem(el, options);
    case 'link':
      return buildLink(el, options);
    case 'text':
      return buildText(el, options);
    default:
      console.warn(`Unknown rich text element type: ${el.type}`);
      return '';
  }
}

/**
 * Build paragraph element
 * @private
 */
function buildParagraph(el, options) {
  const content = convertChildren(el.children, options);
  return createElement('p', options.classes, content);
}

/**
 * Build heading element
 * @private
 */
function buildHeading(el, options) {
  const tag = `h${el.level || 2}`;
  const content = convertChildren(el.children, options);
  return createElement(tag, options.classes, content);
}

/**
 * Build list element
 * @private
 */
function buildList(el, options) {
  const tag = el.listType === 'ordered' ? 'ol' : 'ul';
  const content = convertChildren(el.children, options);
  return createElement(tag, options.classes, content);
}

/**
 * Build list item element
 * @private
 */
function buildListItem(el, options) {
  const content = convertChildren(el.children, options);
  return createElement('li', options.classes, content);
}

/**
 * Build link element
 * @private
 */
function buildLink(el, options) {
  const content = convertChildren(el.children, options);
  const attributes = {};

  if (el.url) attributes.href = el.url;
  if (el.title) attributes.title = el.title;
  if (el.target) attributes.target = el.target;

  // Add rel="noopener noreferrer" for external links with target
  if (el.target === '_blank') {
    attributes.rel = 'noopener noreferrer';
  }

  return createElement('a', options.classes, content, attributes);
}

/**
 * Build text element with formatting
 * @private
 */
function buildText(el, options) {
  let value = el.value || '';

  // Convert newlines to <br> if requested
  if (options.newLineToBreak) {
    value = value.replace(/\n/g, '<br>');
  }

  // Escape HTML to prevent XSS
  value = escapeHtml(value);

  // Apply text formatting
  if (el.bold && el.italic) {
    return createElement('strong', options.classes, createElement('em', options.classes, value));
  } else if (el.bold) {
    return createElement('strong', options.classes, value);
  } else if (el.italic) {
    return createElement('em', options.classes, value);
  }

  return value;
}

/**
 * Create HTML element with class and attributes
 * @private
 */
function createElement(tag, classes, content, attributes = {}) {
  // Add class if specified for this tag
  if (classes && classes[tag]) {
    attributes.class = classes[tag];
  }

  // Build attributes string
  const attrs = Object.entries(attributes)
    .filter(([key, value]) => value != null && value !== '')
    .map(([key, value]) => `${key}="${escapeHtml(String(value))}"`)
    .join(' ');

  const attrsString = attrs ? ` ${attrs}` : '';

  return `<${tag}${attrsString}>${content}</${tag}>`;
}

/**
 * LocationsService Class
 * Manages all server-side communication and data transformation
 */
export class LocationsService {
  /**
   * Initialize the locations service
   * @param {Object} config - Service configuration
   * @param {string} config.shopDomain - Shopify shop domain
   * @param {string} config.apiVersion - Storefront API version
   * @param {string} config.accessToken - Storefront API access token
   * @param {Object} config.translator - Optional translator instance
   */
  constructor({ shopDomain, apiVersion, accessToken, defaultTimezone, translator = null, locationsJsonUrl }) {
    this.shopDomain = shopDomain;
    this.apiVersion = apiVersion;
    this.accessToken = accessToken;
    this.storefrontApiUrl = `https://${shopDomain}/api/${apiVersion}/graphql.json`;
    this.defaultTimezone = defaultTimezone || 'UTC';
    this.locationsJsonUrl = locationsJsonUrl; // Static JSON file URL (from asset_url)

    // Cache configuration
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes in milliseconds
    this.cacheKey = 'store_locator_locations';
    this.cacheTimestampKey = 'store_locator_timestamp';

    // Use provided translator or create default one
    this.translator = translator || createTranslator('store-locator-translations', '');
  }

  /**
   * Fetch all locations from static JSON file with fallback to Storefront API
   * Uses sessionStorage cache with 5-minute TTL
   * @returns {Promise<Array>} Array of transformed location objects
   */
  async getAllLocations() {
    // Try to use cached data first
    const cachedData = this.getCachedData(this.cacheKey);
    if (cachedData) {
      // Recalculate status for each location since it's time-dependent
      return cachedData.map(location => ({
        ...location,
        status: this.calculateStoreStatus(location.metafields)
      }));
    }

    // Try static JSON file first, fallback to API if it fails
    try {
      const locations = await this.fetchFromStaticJson();
      if (locations && locations.length > 0) {
        return locations;
      }
      // Empty or invalid data, fallback to API
      console.warn('Static locations file is empty or invalid, falling back to Storefront API');
      return await this.fetchFromStorefrontApi();
    } catch (staticError) {
      console.warn('Failed to load from static JSON, falling back to Storefront API:', staticError.message);
      try {
        return await this.fetchFromStorefrontApi();
      } catch (apiError) {
        console.error('Failed to load locations from both static file and API:', apiError);
        throw apiError;
      }
    }
  }

  /**
   * Fetch locations from static JSON file
   * Uses browser cache for optimal performance:
   * - First request: ~20-50ms (preloaded by <link rel="preload">)
   * - Subsequent requests: ~5-10ms (browser disk cache)
   * - With sessionStorage: ~1-5ms (memory cache)
   * @private
   */
  async fetchFromStaticJson() {
    if (!this.locationsJsonUrl) {
      throw new Error('No static locations JSON URL configured');
    }

    // Remove cache-busting query parameter to leverage browser cache
    // File is preloaded in theme.liquid for optimal first load performance
    const response = await fetch(this.locationsJsonUrl, {
      cache: 'default', // Respect HTTP Cache-Control headers
    });
    
    if (!response.ok) {
      throw new Error(`Failed to load locations file: ${response.status}`);
    }

    const data = await response.json();
    
    // Validate data structure
    if (!data || !data.locations || !Array.isArray(data.locations)) {
      throw new Error('Invalid locations file structure');
    }

    return this.processStaticLocations(data);
  }

  /**
   * Fetch locations from Storefront API (fallback)
   * @private
   */
  async fetchFromStorefrontApi() {
    const allLocations = [];
    let hasMorePages = true;
    let currentCursor = null;

    // Fetch all pages using pagination
    while (hasMorePages) {
      const variables = {
        first: 250 // Fetch 250 locations at a time
      };

      if (currentCursor) {
        variables.after = currentCursor;
      }

      const response = await fetch(this.storefrontApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.accessToken
        },
        body: JSON.stringify({
          query: this.buildLocationsQuery(),
          variables
        })
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();

      if (data.errors) {
        throw new Error(data.errors[0].message);
      }

      const locationsData = data.data.locations;
      const locations = this.transformLocations(locationsData.edges);

      // Update pagination info
      currentCursor = locationsData.pageInfo.endCursor;
      hasMorePages = locationsData.pageInfo.hasNextPage;

      // Append locations, deduplicating by ID
      const existingIds = new Set(allLocations.map(location => location.id));
      const newLocations = locations.filter(location => !existingIds.has(location.id));
      allLocations.push(...newLocations);
    }

    // Cache the results (without status, as it will be recalculated on read)
    const locationsToCache = allLocations.map(location => {
      const { status, ...locationWithoutStatus } = location;
      return locationWithoutStatus;
    });
    this.setCachedData(this.cacheKey, locationsToCache);

    return allLocations;
  }

  /**
   * Process static locations JSON data
   * @private
   */
  processStaticLocations(data) {
    // Handle both formats: { locations: [...] } or { metadata: {...}, locations: [...] }
    const rawLocations = data.locations || [];
    
    // Transform static format to service format
    const allLocations = rawLocations.map(loc => this.transformStaticLocation(loc));

    // Cache the results (without status, as it will be recalculated on read)
    const locationsToCache = allLocations.map(location => {
      const { status, ...locationWithoutStatus } = location;
      return locationWithoutStatus;
    });
    this.setCachedData(this.cacheKey, locationsToCache);

    return allLocations;
  }

  /**
   * Transform static JSON location to service format
   * Static JSON is generated from Admin API Location.toJSON()
   * @private
   */
  transformStaticLocation(loc) {
    // Static file metafields - map to same format as transformLocation()
    const metafields = loc.metafields || {};
    
    const transformedMetafields = {
      openingHours: metafields.openingHours || null,
      openingHoursNote: metafields.openingHoursNote || null,
      deliveryService: metafields.deliveryService || metafields.deliveryServices || null,
      holidayOpeningHours: metafields.holidayOpeningHours || null,
      temporaryClosure: metafields.temporaryClosure || null,
      description: metafields.description || null,
      bakeryOperator: metafields.bakeryOperator || null,
      timezone: metafields.timezone || this.defaultTimezone
    };

    const storeName = metafields.publicStoreName || loc.name;

    // Parse coordinates and validate
    const latitude = loc.coordinates?.latitude || loc.address?.latitude;
    const longitude = loc.coordinates?.longitude || loc.address?.longitude;

    // Validate coordinates are valid numbers
    if (!latitude || !longitude || isNaN(latitude) || isNaN(longitude)) {
      console.warn(
        `Location "${storeName}" (${loc.id}) has invalid coordinates:`,
        { latitude, longitude }
      );
    }

    const transformedData = {
      id: loc.id,
      name: storeName,
      slug: slugify(storeName),
      address: {
        address1: loc.address?.address1 || '',
        address2: loc.address?.address2 || '',
        city: loc.address?.city || '',
        province: loc.address?.province || '',
        provinceCode: loc.address?.provinceCode || '',
        zip: loc.address?.zip || '',
        country: loc.address?.country || '',
        countryCode: loc.address?.countryCode || '',
        formatted: loc.address?.formatted || []
      },
      phone: loc.address?.phone || '',
      coordinates: {
        latitude: loc.coordinates?.latitude || loc.address?.latitude,
        longitude: loc.coordinates?.longitude || loc.address?.longitude
      },
      metafields: transformedMetafields
    };

    // Calculate and attach status
    transformedData.status = this.calculateStoreStatus(transformedMetafields);

    return transformedData;
  }

  /**
   * Fetch single location by ID
   * Note: Shopify Storefront API doesn't support single location query,
   * so we fetch all locations and filter by ID
   * @param {string} id - Location ID (can be short "123" or full "gid://shopify/Location/123")
   * @returns {Promise<Object>} Transformed location object with fresh status
   */
  async getLocationById(id) {
    try {
      // Get all locations (uses cache if available, with recalculated status)
      const allLocations = await this.getAllLocations();

      // Convert short ID to full GID if needed
      const fullId = id.startsWith('gid://')
        ? id
        : `gid://shopify/Location/${id}`;

      // Find location by ID
      const location = allLocations.find(loc => loc.id === fullId);

      if (!location) {
        throw new Error(`Location not found: ${id}`);
      }

      return location;

    } catch (error) {
      console.error('Failed to load location by ID:', error);
      throw error;
    }
  }

  /**
   * Fetch single location by slug
   * @param {string} slug - Location slug (e.g., "warriewood-bakery")
   * @returns {Promise<Object>} Transformed location object with fresh status
   */
  async getLocationBySlug(slug) {
    try {
      // Get all locations (uses cache if available, with recalculated status)
      const allLocations = await this.getAllLocations();

      // Find location by slug
      const location = allLocations.find(loc => loc.slug === slug);

      if (!location) {
        throw new Error(`Location not found: ${slug}`);
      }

      return location;

    } catch (error) {
      console.error('Failed to load location by slug:', error);
      throw error;
    }
  }

  /**
   * Search locations by name (client-side filtering)
   * @param {string} query - Search query
   * @returns {Promise<Array>} Array of matching locations
   */
  async searchLocationsByName(query) {
    try {
      const allLocations = await this.getAllLocations();
      const queryLower = query.toLowerCase();

      return allLocations
        .filter(location => location.name.toLowerCase().includes(queryLower))
        .slice(0, 3); // Max 3 results for autocomplete

    } catch (error) {
      console.error('Store name search failed:', error);
      return [];
    }
  }

  /**
   * Build GraphQL query for fetching all locations from Storefront API
   * @private
   */
  buildLocationsQuery() {
    return `
      query getLocations($first: Int!, $after: String) {
        locations(first: $first, after: $after) {
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
          edges {
            cursor
            node {
              id
              name
              address {
                address1
                address2
                city
                province
                provinceCode
                zip
                country
                countryCode
                phone
                latitude
                longitude
                formatted
              }
              metafields(identifiers: [
                {namespace: "amb", key: "public_store_name"},
                {namespace: "amb", key: "opening_hours"},
                {namespace: "amb", key: "opening_hours_note"},
                {namespace: "amb", key: "delivery_service"},
                {namespace: "amb", key: "public_holiday_special_opening_hours"},
                {namespace: "amb", key: "temporary_closure"},
                {namespace: "amb", key: "description"},
                {namespace: "amb", key: "bakery_operator"},
                {namespace: "amb", key: "timezone"}
              ]) {
                key
                value
                type
              }
            }
          }
        }
      }
    `;
  }


  /**
   * Transform array of GraphQL location edges to standardized format
   * @private
   */
  transformLocations(edges) {
    return edges.map(edge => this.transformLocation(edge.node));
  }

  /**
   * Transform single GraphQL location node to standardized format
   * @private
   */
  transformLocation(node) {
    // Parse metafields into object
    const metafields = {};
    if (node.metafields && Array.isArray(node.metafields)) {
      node.metafields.forEach(mf => {
        if (!mf || !mf.key) return; // Skip null or invalid metafields

        let value = mf.value;
        // Parse JSON metafields
        if (mf.type === 'json' && value) {
          try {
            value = JSON.parse(value);
          } catch (e) {
            console.warn(`Failed to parse JSON metafield ${mf.key}:`, e);
          }
        }
        metafields[mf.key] = value;
      });
    }

    const storeName = metafields.public_store_name || node.name;

    const transformedData = {
      id: node.id,
      name: storeName,
      slug: slugify(storeName),
      address: {
        address1: node.address.address1,
        address2: node.address.address2,
        city: node.address.city,
        province: node.address.province,
        provinceCode: node.address.provinceCode,
        zip: node.address.zip,
        country: node.address.country,
        countryCode: node.address.countryCode,
        formatted: node.address.formatted
      },
      phone: node.address.phone,
      coordinates: {
        latitude: node.address.latitude,
        longitude: node.address.longitude
      },
      metafields: {
        openingHours: metafields.opening_hours || null,
        openingHoursNote: metafields.opening_hours_note || null,
        deliveryService: metafields.delivery_service || null, // Fixed: Use singular key
        holidayOpeningHours: metafields.public_holiday_special_opening_hours || null,
        temporaryClosure: metafields.temporary_closure || null,
        description: metafields.description || null,
        bakeryOperator: metafields.bakery_operator || null,
        timezone: metafields.timezone || this.defaultTimezone
      }
    };

    // Calculate and attach status
    transformedData.status = this.calculateStoreStatus(transformedData.metafields);

    return transformedData;
  }

  /**
   * Calculate store open/closed status from metafields
   * @param {Object} metafields - Store metafields object
   * @returns {Object} Status object with isOpen, isTemporarilyClosed, statusText, nextStateChangeText
   */
  calculateStoreStatus(metafields) {
    let isOpen = false;
    let isTemporarilyClosed = false;
    let statusText = 'Closed';
    let nextStateChange = null;

    // Get the bakery's timezone (fallback to default if not set)
    const timezone = metafields?.timezone || this.defaultTimezone;

    // Check temporary closure first
    if (metafields?.temporaryClosure?.temporarily_close_bakery) {
      const closure = metafields.temporaryClosure;
      const endDate = closure.temporary_close_end_date;
      
      // Check if end date has passed
      let closureStillActive = true;
      if (endDate && endDate !== 'null') {
        const endDateTime = this.parseDate(endDate, timezone);
        const now = this.getCurrentTime(timezone);
        
        // If end date exists and has passed, closure is no longer active
        if (endDateTime && now >= endDateTime) {
          closureStillActive = false;
        }
      }
      
      if (closureStillActive) {
        isTemporarilyClosed = true;
        statusText = closure.bakery_closure_reason || 'Temporarily Closed';

        // Parse end date for next state change (in bakery's timezone)
        if (endDate && endDate !== 'null') {
          nextStateChange = this.parseDate(endDate, timezone);
        }
      }
    }
    
    // Only check regular hours if not temporarily closed
    if (!isTemporarilyClosed) {
      // Get current time in the bakery's timezone
      const now = this.getCurrentTime(timezone);
      const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const todayKey = daysOfWeek[now.getDay()];
      const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

      // Check holiday hours first
      const holidayHours = this.getTodaysHolidayHours(metafields?.holidayOpeningHours, timezone);
      if (holidayHours) {
        if (holidayHours.status === 'closed') {
          statusText = holidayHours.name;
          nextStateChange = this.calculateNextOpening(
            now,
            metafields?.openingHours,
            metafields?.holidayOpeningHours,
            metafields?.temporaryClosure,
            timezone
          );
        } else {
          isOpen = this.isTimeWithinHours(currentTime, holidayHours.open, holidayHours.close);
          statusText = holidayHours.name;
          if (isOpen && holidayHours.close) {
            nextStateChange = this.parseTimeToday(holidayHours.close, timezone);
          }
        }
      } else {
        // Check regular hours
        const regularHours = metafields?.openingHours?.[todayKey];
        if (!regularHours || regularHours.status === 'closed') {
          nextStateChange = this.calculateNextOpening(
            now,
            metafields?.openingHours,
            metafields?.holidayOpeningHours,
            metafields?.temporaryClosure,
            timezone
          );
        } else {
          isOpen = this.isTimeWithinHours(currentTime, regularHours.open, regularHours.close);
          statusText = isOpen ? 'Open' : 'Closed';

          // Calculate next state change
          if (isOpen && regularHours.close) {
            nextStateChange = this.parseTimeToday(regularHours.close, timezone);
          } else if (!isOpen) {
            if (regularHours.open && this.timeToMinutes(currentTime) < this.timeToMinutes(regularHours.open)) {
              nextStateChange = this.parseTimeToday(regularHours.open, timezone);
            } else {
              nextStateChange = this.calculateNextOpening(
                now,
                metafields?.openingHours,
                metafields?.holidayOpeningHours,
                metafields?.temporaryClosure,
                timezone
              );
            }
          }
        }
      }
    }

    return {
      isOpen,
      isTemporarilyClosed,
      statusText,
      nextStateChangeData: this.formatNextStateChange(
        nextStateChange,
        isOpen,
        isTemporarilyClosed,
        metafields?.openingHours,
        timezone
      )
    };
  }

  /**
   * Format next state change into translation key and arguments
   * Returns an object with translation key and ALL possible args for maximum flexibility
   * @private
   * @param {Date} nextStateChange - Next state change date
   * @param {boolean} isOpen - Whether store is currently open
   * @param {boolean} isTemporarilyClosed - Whether store is temporarily closed
   * @param {Object} openingHours - Opening hours object with day schedules
   * @param {string} timezone - Bakery's timezone (IANA format)
   * @returns {Object} Object with { key: string, args: object } or null for UI to translate
   */
  formatNextStateChange(nextStateChange, isOpen, isTemporarilyClosed, openingHours, timezone) {
    if (!nextStateChange) return null;

    const changeDate = new Date(nextStateChange);
    const now = this.getCurrentTime(timezone);

    // Format the time in 24-hour format (e.g., "17:00")
    const timeHours = changeDate.getHours();
    const timeMinutes = changeDate.getMinutes();
    const time24h = `${timeHours.toString().padStart(2, '0')}:${timeMinutes.toString().padStart(2, '0')}`;

    // Format the time in 12-hour format with lowercase am/pm, no space (e.g., "5:00pm")
    const hour12 = timeHours % 12 || 12;
    const ampm = timeHours >= 12 ? 'pm' : 'am';
    const time12h = `${hour12}:${timeMinutes.toString().padStart(2, '0')}${ampm}`;

    // Check if it's today, tomorrow, or later (comparing in bakery's timezone)
    const isToday = changeDate.toDateString() === now.toDateString();
    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);
    const isTomorrow = changeDate.toDateString() === tomorrow.toDateString();

    // Get full day name (e.g., "Monday")
    const dayIso = changeDate.toLocaleDateString('en-US', {
      weekday: 'long'
    });

    // Get short day name (e.g., "Mon")
    const dayIsoShort = changeDate.toLocaleDateString('en-US', {
      weekday: 'short'
    });

    // Determine human-friendly "day" value
    let dayText;
    if (isToday) {
      dayText = 'today';
    } else if (isTomorrow) {
      dayText = 'tomorrow';
    } else {
      dayText = dayIso;
    }

    // Get the opening hours for the next state change day (not today!)
    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const nextStateDay = daysOfWeek[changeDate.getDay()];
    const nextStateDayHours = openingHours?.[nextStateDay];

    // Parse the hours for that day
    let hoursString = null;
    if (nextStateDayHours && nextStateDayHours.status !== 'closed' && nextStateDayHours.open && nextStateDayHours.close) {
      hoursString = `${nextStateDayHours.open}-${nextStateDayHours.close}`;
    }

    // Parse opening hours into separate open/close times if available
    let openTime24h = '';
    let openTime12h = '';
    let closeTime24h = '';
    let closeTime12h = '';

    if (hoursString) {
      // Parse "09:00-17:00" format
      const [start, end] = hoursString.split('-');
      if (start && end) {
        const [startHour, startMin] = start.split(':').map(Number);
        const [endHour, endMin] = end.split(':').map(Number);

        // 24h format
        openTime24h = `${startHour.toString().padStart(2, '0')}:${startMin.toString().padStart(2, '0')}`;
        closeTime24h = `${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`;

        // 12h format with lowercase am/pm, no space
        const startHour12 = startHour % 12 || 12;
        const startAmpm = startHour >= 12 ? 'pm' : 'am';
        const endHour12 = endHour % 12 || 12;
        const endAmpm = endHour >= 12 ? 'pm' : 'am';

        openTime12h = `${startHour12}:${startMin.toString().padStart(2, '0')}${startAmpm}`;
        closeTime12h = `${endHour12}:${endMin.toString().padStart(2, '0')}${endAmpm}`;
      }
    }

    // Get month name and date number for full date format
    const monthName = changeDate.toLocaleDateString('en-US', { month: 'long' }); // "December"
    const dateNumber = changeDate.getDate(); // 31

    // Build comprehensive args object
    const args = {
      open_time: openTime24h || time24h,        // 24h format: "09:00"
      open_time_12h: openTime12h || time12h,    // 12h format: "9:00am"
      close_time: closeTime24h,                 // 24h format: "17:00"
      close_time_12h: closeTime12h,             // 12h format: "5:00pm"
      day_iso: dayIso,                          // Full day: "Monday"
      day_iso_short: dayIsoShort,               // Short day: "Mon"
      day: dayText,                             // "today", "tomorrow", or day name
      timezone: timezone,                       // Bakery timezone: "Australia/Sydney"
      month: monthName,                         // Full month name: "December"
      date: dateNumber                          // Date number: 31
    };

    let message_key = null;

    if (isTemporarilyClosed) {
      if (isToday) {
        message_key = 'location.status.reopens_today_at';
      } else if (isTomorrow) {
        message_key = 'location.status.reopens_tomorrow_at';
      } else {
        message_key = 'location.status.reopens_on_date_at';
      }
    } else if (isOpen) {
      message_key = 'location.status.closes_at';
    } else {
      if (isToday) {
        message_key = 'location.status.opens_today_at';
      } else if (isTomorrow) {
        message_key = 'location.status.opens_tomorrow_at';
      } else {
        message_key = 'location.status.opens_on_day_at';
      }
    }

    // Return object with key and args for UI to translate
    // This ensures translator is ready when translation happens
    return {
      key: message_key,
      args: args
    };
  }

  /**
   * Get today's holiday hours if applicable
   * @private
   */
  getTodaysHolidayHours(holidayOpeningHours, timezone) {
    if (!holidayOpeningHours) return null;

    const today = this.getCurrentTime(timezone);
    const todayStr = `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}`;

    for (const [holidayName, holidayData] of Object.entries(holidayOpeningHours)) {
      if (holidayData.date === todayStr) {
        return {
          ...holidayData,
          name: holidayName // Include the holiday name for display
        };
      }
    }

    return null;
  }

  /**
   * Calculate when the store will next open
   * Takes into account temporary closures, holiday hours, and regular hours
   * @private
   */
  calculateNextOpening(currentDate, openingHours, holidayOpeningHours, temporaryClosure, timezone) {
    // If temporarily closed, return the end date of the closure (parsed in bakery's timezone)
    if (temporaryClosure?.temporarily_close_bakery) {
      const endDate = temporaryClosure.temporary_close_end_date;
      if (endDate && endDate !== 'null') {
        return this.parseDate(endDate, timezone);
      }
      return null;
    }

    if (!openingHours) return null;

    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const currentDay = currentDate.getDay();

    // Check next 7 days for an opening time
    for (let i = 1; i <= 7; i++) {
      const nextDate = new Date(currentDate);
      nextDate.setDate(currentDate.getDate() + i);

      const nextDayIndex = (currentDay + i) % 7;
      const nextDayKey = daysOfWeek[nextDayIndex];

      // Check if this day has holiday hours
      const dateStr = `${nextDate.getFullYear()}-${(nextDate.getMonth() + 1).toString().padStart(2, '0')}-${nextDate.getDate().toString().padStart(2, '0')}`;
      let hasHolidayHours = false;
      let holidayHours = null;

      if (holidayOpeningHours) {
        for (const [holidayName, holidayData] of Object.entries(holidayOpeningHours)) {
          if (holidayData.date === dateStr) {
            hasHolidayHours = true;
            holidayHours = { ...holidayData, name: holidayName };
            break;
          }
        }
      }

      // If there are holiday hours, check if store opens on this holiday
      if (hasHolidayHours) {
        if (holidayHours.status !== 'closed' && holidayHours.open) {
          const [hours, minutes] = holidayHours.open.split(':').map(Number);
          nextDate.setHours(hours, minutes, 0, 0);
          return nextDate;
        }
        // Holiday is closed, continue to next day
        continue;
      }

      // Check regular hours for this day
      const nextDayHours = openingHours[nextDayKey];

      // Check if day has opening hours (either has explicit status='open' or has open/close times)
      if (nextDayHours && nextDayHours.open && (nextDayHours.status !== 'closed')) {
        const [hours, minutes] = nextDayHours.open.split(':').map(Number);
        nextDate.setHours(hours, minutes, 0, 0);

        return nextDate;
      }
    }

    return null;
  }

  /**
   * Check if current time is within business hours
   * @private
   */
  isTimeWithinHours(currentTime, openTime, closeTime) {
    if (!openTime || !closeTime) return false;

    const current = this.timeToMinutes(currentTime);
    const open = this.timeToMinutes(openTime);
    const close = this.timeToMinutes(closeTime);

    // Handle overnight hours (e.g., open until 2:00 AM)
    if (close < open) {
      return current >= open || current <= close;
    }

    return current >= open && current <= close;
  }

  /**
   * Convert time string (HH:MM) to minutes since midnight
   * @private
   */
  timeToMinutes(timeString) {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  }

  /**
   * Parse time string (HH:MM) to a Date object for today in the specified timezone
   * @private
   */
  parseTimeToday(timeString, timezone) {
    const [hours, minutes] = timeString.split(':').map(Number);
    const date = this.getCurrentTime(timezone);
    date.setHours(hours, minutes, 0, 0);
    return date;
  }

  /**
   * Get current time in the specified timezone
   * @private
   */
  getCurrentTime(timezone) {
    const now = new Date();
    
    // Use Intl.DateTimeFormat to get the date/time components in the target timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    const parts = formatter.formatToParts(now);
    const year = parts.find(p => p.type === 'year').value;
    const month = parts.find(p => p.type === 'month').value;
    const day = parts.find(p => p.type === 'day').value;
    const hour = parts.find(p => p.type === 'hour').value;
    const minute = parts.find(p => p.type === 'minute').value;
    const second = parts.find(p => p.type === 'second').value;
    
    // Create a Date object using the constructor with components
    // This ensures getHours(), getDate(), etc. return the target timezone values
    return new Date(
      parseInt(year),
      parseInt(month) - 1, // Month is 0-indexed
      parseInt(day),
      parseInt(hour),
      parseInt(minute),
      parseInt(second)
    );
  }

  /**
   * Parse a date string (YYYY-MM-DD or ISO format) in the specified timezone
   * @private
   * @param {string} dateString - Date string to parse
   * @param {string} timezone - Timezone to parse the date in
   * @returns {Date} Date object representing the date at midnight in the specified timezone
   */
  parseDate(dateString, timezone) {
    if (!dateString) return null;

    // If it's an ISO datetime string with timezone info, use it directly
    if (dateString.includes('T') && (dateString.includes('Z') || dateString.includes('+'))) {
      return new Date(dateString);
    }

    // For date-only strings (YYYY-MM-DD), treat as midnight in the bakery's timezone
    // Parse the date components
    const [year, month, day] = dateString.split('-').map(Number);

    // Create a Date object representing midnight in the target timezone
    // Using Date constructor with components to ensure proper timezone handling
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  /**
   * Get cached data from sessionStorage
   * @private
   */
  getCachedData(key) {
    try {
      const cachedData = sessionStorage.getItem(key);
      const cachedTimestamp = sessionStorage.getItem(this.cacheTimestampKey);

      if (cachedData && cachedTimestamp) {
        const age = Date.now() - parseInt(cachedTimestamp, 10);
        if (age < this.cacheExpiry) {
          return JSON.parse(cachedData);
        }
      }
    } catch (e) {
      // Cache read failed, return null
      console.warn('Failed to read cache:', e);
    }

    return null;
  }

  /**
   * Set cached data in sessionStorage
   * @private
   */
  setCachedData(key, data) {
    try {
      sessionStorage.setItem(key, JSON.stringify(data));
      sessionStorage.setItem(this.cacheTimestampKey, Date.now().toString());
    } catch (e) {
      // Cache write failed (quota exceeded or disabled), continue without caching
      console.warn('Failed to write cache:', e);
    }
  }

  /**
   * Clear all cached data
   */
  clearCache() {
    try {
      sessionStorage.removeItem(this.cacheKey);
      sessionStorage.removeItem(this.cacheTimestampKey);
    } catch (e) {
      console.warn('Failed to clear cache:', e);
    }
  }

  /**
   * Extract short ID from full Shopify GID
   * @param {string} gid - Full GID (e.g., "gid://shopify/Location/80049832128")
   * @returns {string} Short ID (e.g., "80049832128")
   */
  static extractShortId(gid) {
    if (!gid) return '';

    // If already short ID, return as-is
    if (!gid.startsWith('gid://')) {
      return gid;
    }

    // Extract numeric ID from GID format
    const match = gid.match(/gid:\/\/shopify\/Location\/(\d+)/);
    return match ? match[1] : gid;
  }
}

